import React, { useEffect, useState } from 'react';
import { Box, Text, FormControl, FormLabel, Input, Select, Button, SimpleGrid, Divider, Checkbox, Flex } from '@chakra-ui/react';
import { useBoardContext } from '../context/BoardContext';
import { boardMembers } from '../components/BoardSidebar';
const TEAL = '#00897B';
const BORDER_COLOR = '#006666';
const terms = ['1st', '2nd', '3rd'];
const officeOptions = ['President', 'Vice President'];
function parseName(fullName: string): {
  firstName: string;
  lastName: string;
} {
  if (!fullName || fullName === 'Vacant') return {
    firstName: '',
    lastName: ''
  };
  const parts = fullName.trim().split(' ');
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ') || '';
  return {
    firstName,
    lastName
  };
}
const inputStyles = {
  borderColor: 'gray.300',
  focusBorderColor: TEAL,
  _hover: {
    borderColor: TEAL
  },
  size: 'sm' as const,
  bg: 'white'
};
const labelStyles = {
  fontSize: 'xs' as const,
  fontWeight: '600' as const,
  color: TEAL,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  mb: 0.5
};
export function BoardPage() {
  const {
    selectedIndex
  } = useBoardContext();
  const selected = boardMembers[selectedIndex];
  const {
    firstName: initFirst,
    lastName: initLast
  } = parseName(selected.name);
  const [firstName, setFirstName] = useState(initFirst);
  const [lastName, setLastName] = useState(initLast);
  const [company, setCompany] = useState('');
  const [homePhone, setHomePhone] = useState('');
  const [cell, setCell] = useState('');
  const [email, setEmail] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [term, setTerm] = useState(terms[0]);
  const [startDate, setStartDate] = useState('');
  const [termEndDate, setTermEndDate] = useState('');
  const [isOfficer, setIsOfficer] = useState(false);
  const [office, setOffice] = useState(officeOptions[0]);
  useEffect(() => {
    const {
      firstName: f,
      lastName: l
    } = parseName(boardMembers[selectedIndex].name);
    setFirstName(f);
    setLastName(l);
    setCompany('');
    setHomePhone('');
    setCell('');
    setEmail('');
    setStreet('');
    setCity('');
    setState('');
    setZip('');
    setTerm(terms[0]);
    setStartDate('');
    setTermEndDate('');
    setIsOfficer(false);
    setOffice(officeOptions[0]);
  }, [selectedIndex]);
  const handleSave = () => {
    console.log('Saving board member...');
  };
  return <Box px={6} py={3} maxW="700px" data-id="element-183">
      <Text fontSize="xl" fontWeight="bold" color="black" mb={3} data-id="element-184">
        Edit Board Member
      </Text>

      <Box border={`2px solid ${BORDER_COLOR}`} borderRadius="md" bg="white" p={4} data-id="element-185">
        {/* Name row */}
        <SimpleGrid columns={2} spacing={3} mb={3} data-id="element-186">
          <FormControl data-id="element-187">
            <FormLabel {...labelStyles} data-id="element-188">First Name</FormLabel>
            <Input {...inputStyles} value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First Name" data-id="element-189" />
          </FormControl>
          <FormControl data-id="element-190">
            <FormLabel {...labelStyles} data-id="element-191">Last Name</FormLabel>
            <Input {...inputStyles} value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last Name" data-id="element-192" />
          </FormControl>
        </SimpleGrid>

        <FormControl mb={3} data-id="element-193">
          <FormLabel {...labelStyles} data-id="element-194">Company/Farm Name</FormLabel>
          <Input {...inputStyles} value={company} onChange={e => setCompany(e.target.value)} placeholder="Company/Farm Name" data-id="element-195" />
        </FormControl>

        {/* Address block */}
        <FormControl mb={3} data-id="element-196">
          <FormLabel {...labelStyles} data-id="element-197">Street Address</FormLabel>
          <Input {...inputStyles} value={street} onChange={e => setStreet(e.target.value)} placeholder="Street Address" data-id="element-198" />
        </FormControl>

        <SimpleGrid columns={3} spacing={3} mb={3} data-id="element-199">
          <FormControl data-id="element-200">
            <FormLabel {...labelStyles} data-id="element-201">City</FormLabel>
            <Input {...inputStyles} value={city} onChange={e => setCity(e.target.value)} placeholder="City" data-id="element-202" />
          </FormControl>
          <FormControl data-id="element-203">
            <FormLabel {...labelStyles} data-id="element-204">State</FormLabel>
            <Input {...inputStyles} value={state} onChange={e => setState(e.target.value)} placeholder="State" maxLength={2} data-id="element-205" />
          </FormControl>
          <FormControl data-id="element-206">
            <FormLabel {...labelStyles} data-id="element-207">Zip</FormLabel>
            <Input {...inputStyles} value={zip} onChange={e => setZip(e.target.value)} placeholder="Zip" data-id="element-208" />
          </FormControl>
        </SimpleGrid>

        {/* Phone fields */}
        <SimpleGrid columns={2} spacing={3} mb={3} data-id="element-209">
          <FormControl data-id="element-210">
            <FormLabel {...labelStyles} data-id="element-211">Home/Business Phone</FormLabel>
            <Input {...inputStyles} value={homePhone} onChange={e => setHomePhone(e.target.value)} placeholder="Home/Business Phone" type="tel" data-id="element-212" />
          </FormControl>
          <FormControl data-id="element-213">
            <FormLabel {...labelStyles} data-id="element-214">Cell</FormLabel>
            <Input {...inputStyles} value={cell} onChange={e => setCell(e.target.value)} placeholder="Cell" type="tel" data-id="element-215" />
          </FormControl>
        </SimpleGrid>

        {/* Email */}
        <FormControl mb={3} data-id="element-216">
          <FormLabel {...labelStyles} data-id="element-217">Email</FormLabel>
          <Input {...inputStyles} value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" data-id="element-218" />
        </FormControl>

        <Divider borderColor="gray.200" my={3} data-id="element-219" />

        {/* Term + Officer row — flex-end aligns both dropdowns */}
        <Flex gap={3} align="flex-end" mb={3} data-id="element-220">
          <FormControl flex={1} data-id="element-221">
            <FormLabel {...labelStyles} data-id="element-222">Term</FormLabel>
            <Select value={term} onChange={e => setTerm(e.target.value)} borderColor="gray.300" focusBorderColor={TEAL} _hover={{
            borderColor: TEAL
          }} size="sm" bg="white" data-id="element-223">
              {terms.map(t => <option key={t} value={t} data-id="element-224">
                  {t}
                </option>)}
            </Select>
          </FormControl>

          <Box flex={1} data-id="element-225">
            <Checkbox isChecked={isOfficer} onChange={e => setIsOfficer(e.target.checked)} colorScheme="teal" size="md" mb={1} data-id="element-226">
              <Text fontSize="xs" fontWeight="600" color={TEAL} textTransform="uppercase" letterSpacing="0.05em" data-id="element-227">
                This is an Officer
              </Text>
            </Checkbox>
            {isOfficer ? <Select value={office} onChange={e => setOffice(e.target.value)} borderColor="gray.300" focusBorderColor={TEAL} _hover={{
            borderColor: TEAL
          }} size="sm" bg="white" data-id="element-228">
                {officeOptions.map(o => <option key={o} value={o} data-id="element-229">
                    {o}
                  </option>)}
              </Select> /* Invisible placeholder keeps height consistent */ : <Box h="32px" data-id="element-230" />}
          </Box>
        </Flex>

        {/* Dates */}
        <SimpleGrid columns={2} spacing={3} mb={4} data-id="element-231">
          <FormControl data-id="element-232">
            <FormLabel {...labelStyles} data-id="element-233">Term Start Date</FormLabel>
            <Input {...inputStyles} value={startDate} onChange={e => setStartDate(e.target.value)} type="date" data-id="element-234" />
          </FormControl>
          <FormControl data-id="element-235">
            <FormLabel {...labelStyles} data-id="element-236">Term End Date</FormLabel>
            <Input {...inputStyles} value={termEndDate} onChange={e => setTermEndDate(e.target.value)} type="date" data-id="element-237" />
          </FormControl>
        </SimpleGrid>

        {/* Save Button */}
        <Button bg="#0d6dfc" color="white" px={8} size="sm" borderRadius="md" fontWeight="semibold" _hover={{
        bg: '#0b5fd9'
      }} _active={{
        bg: '#0a52be'
      }} onClick={handleSave} data-id="element-238">
          Save
        </Button>
      </Box>
    </Box>;
}